/**
 * Picture tier kit (docs/art-rollout/AUTHORING.md "v3"): shared colour roles + kid-simple background elements for
 * "Vẽ tranh" pictures. Every builder returns ONE named element (a `PicturePart`): its layers all carry the element's
 * part name, so the generated plan can give it its own draw step and colour step. Coordinates: the 240 x 240 picture,
 * light from the top left (shade crescents fall bottom-right, cast shadows go right). Keep 2–5 elements per picture.
 */
import { t, tf, type L } from '../../text';
import { rolePalette } from '../art/palette';
import { blob, ellipse, poly, rotate, scallop } from '../art/geom';
import type { ArtGuide, ArtLayer } from '../art/types';
import type { PictureElement } from '../model';

/** Shared picture roles (unique `pic` prefix; worlds may pass their own roles to any builder). */
export const PICTURE_PALETTE = rolePalette({
  picSky: '#CFE6EE',
  picSkyHigh: '#9CCBE3',
  picMorning: '#F8E9C4',
  picMorningHigh: '#F3D79A',
  picDusk: '#C3B3DD',
  picDuskHigh: '#9583C2',
  picNight: '#46578F',
  picNightHigh: '#2C3567',
  picSunset: '#F8D7BE',
  picSunsetHigh: '#F0AFA6',
  picSpace: '#3A3560',
  picSpaceHigh: '#221E42',
  picRoom: '#F6EBD6',
  picRoomHigh: '#EBD9B9',
  picDeep: '#8FD0D6',
  picDeepHigh: '#5FB2C2',
  picGrass: '#B4D486',
  picGrassNear: '#94C26A',
  picSand: '#F0DCAE',
  picSandNear: '#E3C58E',
  picWood: '#DDAE76',
  picWoodNear: '#C89259',
  picMoonDust: '#D6D2CB',
  picMoonDustNear: '#B9B4AC',
  picSea: '#86CBD3',
  picSeaFar: '#5DAFBF',
  picCloud: '#FFFDF6',
  picHill: '#A3CB7E',
  picHillFar: '#C2DCA7',
  picTrunk: '#A87A4F',
  picLeaf: '#86BC69',
  picLeafDark: '#5E9A57',
  picPine: '#4F8A5B',
  picRock: '#C7BCAE',
  picStone: '#DCD2C3',
  picRed: '#E2674F',
  picPink: '#EE9DA6',
  picYellow: '#F4C64E',
  picPurple: '#B497D6',
  picOrange: '#EF9A4B',
  picCream: '#FFF3DC',
  picBrown: '#8E5E3E',
  picPot: '#D47E55',
  picMoon: '#F6EDC9',
  picEarth: '#6FA9DB',
  picGlow: '#FFF1BF',
  picMountain: '#A9B9D6',
  picSnow: '#FFFFFF',
  picCoral: '#F08C88',
  picSeaweed: '#6DAE76',
  picCurtain: '#F2B9A0',
  picCurtainStripe: '#E9D27C',
  picChair: '#E5C9A0',
  picPlaid: '#D9705A',
  picGlass: '#DCEFF2',
  picFence: '#FFF8EA',
  picRoof: '#C9705A',
  picHouseWall: '#F3DFC1',
  picRiver: '#9BD2DE',
  picFieldA: '#C8DB8C',
  picFieldB: '#E8D27F',
  picFieldC: '#9FC77A',
  picFieldD: '#E7B979',
  picNebula: '#C6A6DE',
  picPlanet: '#E9B27C',
  picRing: '#F3DDB0',
  picBook1: '#7FB0D9',
  picBook2: '#E98C7A',
  picBook3: '#F2C962',
  picTowel: '#F3A06C',
  picBucket: '#9ECBE0',
  picCliff: '#D9B48A',
  picSign: '#5A3E36',
});

/** One authored element of a picture (all layers get `p = part`). */
export interface PicturePart extends PictureElement {
  layers: ArtLayer[];
  guides?: ArtGuide[];
  /** Caption overrides (default: "Draw the *label*" / grouped colour captions). */
  draw?: L;
  colour?: L;
}

const f = (n: number) => Math.round(n * 10) / 10;
export const rrect = (x: number, y: number, w: number, h: number, r: number) =>
  `M${f(x + r)} ${f(y)}L${f(x + w - r)} ${f(y)}Q${f(x + w)} ${f(y)} ${f(x + w)} ${f(y + r)}L${f(x + w)} ${f(y + h - r)}Q${f(x + w)} ${f(y + h)} ${f(x + w - r)} ${f(y + h)}L${f(x + r)} ${f(y + h)}Q${f(x)} ${f(y + h)} ${f(x)} ${f(y + h - r)}L${f(x)} ${f(y + r)}Q${f(x)} ${f(y)} ${f(x + r)} ${f(y)}Z`;
const twinkle = (x: number, y: number, r: number) =>
  `M${x} ${y - r}C${x + r * 0.15} ${y - r * 0.15} ${x + r * 0.15} ${y - r * 0.15} ${x + r} ${y}C${x + r * 0.15} ${y + r * 0.15} ${x + r * 0.15} ${y + r * 0.15} ${x} ${y + r}C${x - r * 0.15} ${y + r * 0.15} ${x - r * 0.15} ${y + r * 0.15} ${x - r} ${y}C${x - r * 0.15} ${y - r * 0.15} ${x - r * 0.15} ${y - r * 0.15} ${x} ${y - r}Z`;
const R = (d: string, fill: string, shade: number | false = 3, extra: Partial<ArtLayer> = {}): ArtLayer => ({ k: 'region', d, fill, shade, ...extra }) as ArtLayer;
const I = (d: string, color: string, w = 0.8): ArtLayer => ({ k: 'ink', d, w, color, taper: 'both' });
const S = (d: string, fill: string, opacity?: number): ArtLayer => ({ k: 'spot', d, fill, ...(opacity !== undefined ? { opacity } : {}) });
/** Where each scenery cast shadow sits and the x of the thing that casts it (pictures lit from the right mirror it). */
const CAST_AT = new WeakMap<ArtLayer, { cx: number; cy: number; rx: number; ry: number; ox: number }>();
export const castInfo = (l: ArtLayer) => CAST_AT.get(l);
/**
 * Cast shadow on the ground (drawn for light from the top left → shadow to the right). Collected in the "light &
 * shadows" step. `ownerX` = x of the thing casting it: a picture lit from the right mirrors the shadow about it.
 */
export const castShadow = (cx: number, cy: number, rx: number, ry = Math.max(3, rx * 0.16), ownerX?: number): ArtLayer => {
  const l: ArtLayer = { k: 'spot', p: 'cast', d: ellipse(cx, cy, rx, ry), fill: 'shade', opacity: 0.3 };
  if (ownerX !== undefined) CAST_AT.set(l, { cx, cy, rx, ry, ox: ownerX });
  return l;
};

function part(p: string, label: L, kind: PictureElement['kind'], layers: ArtLayer[], extra: Partial<PicturePart> = {}): PicturePart {
  return { part: p, label, kind, layers: layers.map((l) => (l.p === 'cast' ? l : { ...l, p })), ...extra, ...(extra.guides ? { guides: extra.guides.map((g) => ({ ...g, p })) } : {}) };
}

/* ------------------------------------------- regions ------------------------------------------- */

const SKY = {
  day: ['picSky', 'picSkyHigh', t('sky', 'bầu trời')],
  morning: ['picMorning', 'picMorningHigh', t('morning sky', 'trời buổi sáng')],
  dusk: ['picDusk', 'picDuskHigh', t('evening sky', 'trời chiều tím')],
  night: ['picNight', 'picNightHigh', t('night sky', 'trời đêm')],
  sunset: ['picSunset', 'picSunsetHigh', t('sunset sky', 'trời hoàng hôn')],
  space: ['picSpace', 'picSpaceHigh', t('space', 'vũ trụ')],
  room: ['picRoom', 'picRoomHigh', t('wall', 'bức tường')],
  water: ['picDeep', 'picDeepHigh', t('sea water', 'nước biển')],
} as const;
export type SkyTone = keyof typeof SKY;

/** The sky (or a room wall / underwater water): a soft two-band gradient, darker at the top. */
export function sky(h: number, tone: SkyTone = 'day', p = 'sky'): PicturePart {
  const [low, high, label] = SKY[tone];
  const inside = tone === 'room' || tone === 'water';
  return part(p, label, 'sky', [R(`M0 0L240 0L240 ${h + 2}L0 ${h + 2}Z`, low, false, { line: false }), R(`M0 0L240 0L240 ${f(h * 0.4)}C170 ${f(h * 0.5)} 70 ${f(h * 0.32)} 0 ${f(h * 0.44)}Z`, high, false, { line: false })], {
    colour: inside
      ? tf('Colour the *{part}*: an even colour, a bit darker at the top', 'Tô *{part}*: màu đều tay, phía trên đậm hơn một chút', { part: label })
      : tf('Colour the *{part}*: stronger at the top, lighter down to the horizon', 'Tô *{part}*: phía trên đậm, xuống gần đường chân trời thì nhạt dần', { part: label }),
  });
}

const GROUND = {
  grass: ['picGrass', 'picGrassNear', t('grass', 'bãi cỏ')],
  sand: ['picSand', 'picSandNear', t('sand', 'bãi cát')],
  wood: ['picWood', 'picWoodNear', t('table top', 'mặt bàn')],
  floor: ['picWood', 'picWoodNear', t('wooden floor', 'sàn gỗ')],
  moon: ['picMoonDust', 'picMoonDustNear', t('moon ground', 'mặt đất mặt trăng')],
  seabed: ['picSand', 'picSandNear', t('sea floor', 'đáy biển')],
} as const;
export type GroundTone = keyof typeof GROUND;

/** The ground below the horizon (its top edge IS the horizon line): far part light, near part stronger. */
export function ground(h: number, tone: GroundTone = 'grass', curve = 0, p = 'ground'): PicturePart {
  const [far, near, label] = GROUND[tone];
  const mid = h + (240 - h) * 0.55;
  return part(
    p,
    label,
    'ground',
    [R(`M0 ${h}C80 ${h - curve} 160 ${h + curve} 240 ${h}L240 240L0 240Z`, far, false), R(`M0 ${f(mid)}C80 ${f(mid - 5)} 160 ${f(mid + 4)} 240 ${f(mid - 2)}L240 240L0 240Z`, near, false, { line: false })],
    { colour: tf('Colour the *{part}*: soft far away, stronger close to you', 'Tô *{part}*: ở xa nhạt, lại gần thì đậm hơn', { part: label }) },
  );
}

/** A band of sea / lake / river water between y0 (far edge) and y1, with ripples. */
export function water(y0: number, y1: number, label = t('sea', 'biển'), p = 'sea', fill = 'picSea', far = 'picSeaFar'): PicturePart {
  const m = y0 + (y1 - y0) * 0.35;
  return part(p, label, 'scenery', [
    R(`M0 ${y0}L240 ${y0}L240 ${y1}C160 ${y1 + 3} 80 ${y1 - 3} 0 ${y1}Z`, fill, false),
    R(`M0 ${y0}L240 ${y0}L240 ${f(m)}C160 ${f(m + 2)} 80 ${f(m - 2)} 0 ${f(m)}Z`, far, false, { line: false }),
    I(`M40 ${f(m + 6)}C48 ${f(m + 3)} 56 ${f(m + 3)} 64 ${f(m + 6)}M168 ${f(m + 9)}C176 ${f(m + 6)} 184 ${f(m + 6)} 192 ${f(m + 9)}`, 'white', 0.7),
  ]);
}

/* ------------------------------------------- nature ------------------------------------------- */

/** Rolling hills sitting on the horizon: [x0, x1, top][]. */
export function hills(h: number, list: [number, number, number][], fill = 'picHill', label = t('hills', 'đồi xa'), p = 'hills'): PicturePart {
  return part(p, label, 'scenery', list.map(([a, b, top]) => R(`M${a} ${h + 1}C${f(a + (b - a) * 0.2)} ${top} ${f(a + (b - a) * 0.8)} ${top} ${b} ${h + 1}Z`, fill, false)));
}
export const cloud = (list: [number, number, number][], p = 'clouds'): PicturePart =>
  part(p, t('clouds', 'mây'), 'scenery', list.map(([x, y, r]) => R(scallop(x, y, r, r * 0.55, 6), 'picCloud', 2)));
/** Round trees: [x, baseY, height][]. */
export function trees(list: [number, number, number][], p = 'trees', crown = 'picLeaf'): PicturePart {
  return part(p, t('trees', 'cây'), 'scenery', list.flatMap(([x, y, h]) => [R(rrect(x - h * 0.06, y - h * 0.5, h * 0.12, h * 0.5, 2), 'picTrunk', 2), R(scallop(x, y - h * 0.66, h * 0.32, h * 0.3, 7), crown, 6), castShadow(x + h * 0.14, y + 1, h * 0.3, undefined, x)]));
}
export function pines(list: [number, number, number][], p = 'pines'): PicturePart {
  return part(p, t('pine trees', 'cây thông'), 'scenery', list.flatMap(([x, y, h]) => [
    R(rrect(x - 3, y - h * 0.18, 6, h * 0.18, 1), 'picTrunk', false),
    R(poly([[x, y - h], [x + h * 0.22, y - h * 0.55], [x + h * 0.13, y - h * 0.55], [x + h * 0.3, y - h * 0.16], [x - h * 0.3, y - h * 0.16], [x - h * 0.13, y - h * 0.55], [x - h * 0.22, y - h * 0.55]]), 'picPine', 4),
  ]));
}
export const bushes = (list: [number, number, number][], p = 'bushes', fill = 'picLeafDark', label = t('bushes', 'bụi cây')): PicturePart =>
  part(p, label, 'scenery', list.map(([x, y, r]) => R(scallop(x, y - r * 0.5, r, r * 0.6, 5, 0.3), fill, 4)));
/** Flowers on stems: [x, groundY][], head colour. */
export function flowers(list: [number, number][], fill = 'picYellow', p = 'flowers', label = t('flowers', 'hoa')): PicturePart {
  return part(p, label, 'scenery', list.flatMap(([x, y]) => [I(`M${x} ${y}C${x - 1} ${y - 6} ${x + 1} ${y - 10} ${x} ${y - 14}`, 'picLeafDark', 0.9), R(scallop(x, y - 17, 5.5, 5.5, 5), fill, false), S(ellipse(x, y - 17, 2), 'picOrange')]));
}
export function tulips(list: [number, number, string][], p = 'tulips'): PicturePart {
  return part(p, t('tulips', 'hoa tulip'), 'scenery', list.flatMap(([x, y, fill]) => [
    I(`M${x} ${y}L${x} ${y - 30}`, 'picLeafDark', 1),
    R(blob([[x, y - 2], [x + 7, y - 14], [x + 2, y - 22]], true, 0.8), 'picLeaf', false),
    R(`M${x - 7} ${y - 40}L${x - 3} ${y - 34}L${x} ${y - 40}L${x + 3} ${y - 34}L${x + 7} ${y - 40}C${x + 8} ${y - 30} ${x + 5} ${y - 26} ${x} ${y - 26}C${x - 5} ${y - 26} ${x - 8} ${y - 30} ${x - 7} ${y - 40}Z`, fill, 2),
  ]));
}
export function sunflowers(x: number, y: number, n: number, p = 'sunflowers'): PicturePart {
  const out: ArtLayer[] = [];
  for (let i = 0; i < n; i++) {
    const cx = x + i * 16;
    const cy = y + (i % 2) * 14;
    out.push(R(blob([[cx - 2, cy + 20], [cx - 16, cy + 26], [cx - 8, cy + 14]], true, 0.8), 'picLeaf', false), R(scallop(cx, cy, 11, 11, 10), 'picYellow', 3), R(ellipse(cx, cy, 4.5), 'picBrown', false));
  }
  return part(p, t('sunflowers', 'hoa hướng dương'), 'scenery', out);
}
export function mushrooms(list: [number, number, number][], p = 'mushrooms'): PicturePart {
  return part(p, t('giant mushrooms', 'nấm khổng lồ'), 'scenery', list.flatMap(([x, y, s]) => [
    R(`M${x - 4 * s} ${y}C${x - 4 * s} ${y - 10 * s} ${x - 3 * s} ${y - 18 * s} ${x - 3 * s} ${y - 22 * s}L${x + 3 * s} ${y - 22 * s}C${x + 3 * s} ${y - 18 * s} ${x + 4 * s} ${y - 10 * s} ${x + 4 * s} ${y}Z`, 'picCream', 2),
    R(`M${x - 13 * s} ${y - 20 * s}C${x - 12 * s} ${y - 33 * s} ${x + 12 * s} ${y - 33 * s} ${x + 13 * s} ${y - 20 * s}C${x + 6 * s} ${y - 17 * s} ${x - 6 * s} ${y - 17 * s} ${x - 13 * s} ${y - 20 * s}Z`, 'picOrange', 4),
    S(ellipse(x - 5 * s, y - 26 * s, 1.8 * s), 'white'),
    S(ellipse(x + 4 * s, y - 28 * s, 1.5 * s), 'white'),
    castShadow(x + 4 * s, y + 1, 9 * s, undefined, x),
  ]));
}
export function ferns(list: [number, number, number][], p = 'ferns'): PicturePart {
  return part(p, t('ferns', 'cây dương xỉ'), 'scenery', list.flatMap(([x, y, dir]) => [
    R(blob([[x, y], [x + dir * 10, y - 18], [x + dir * 24, y - 30], [x + dir * 16, y - 12]], true, 0.9), 'picLeafDark', 2),
    I(`M${x} ${y}C${x + dir * 8} ${y - 12} ${x + dir * 14} ${y - 20} ${x + dir * 22} ${y - 28}`, 'picLeaf', 0.6),
  ]));
}
/** Tall grass / wheat stalks bending in the wind: [x, groundY, height][]. */
export function tallGrass(list: [number, number, number][], p = 'wheat', label = t('wheat stalks', 'bông lúa mì'), fill = 'picFieldB'): PicturePart {
  return part(p, label, 'scenery', list.flatMap(([x, y, h]) => [
    I(`M${x} ${y}C${x + 2} ${y - h * 0.5} ${x + 5} ${y - h * 0.8} ${x + 9} ${y - h}`, 'picLeafDark', 0.8),
    R(blob([[x + 6, y - h * 0.82], [x + 13, y - h * 1.15], [x + 10, y - h * 0.86]], true, 0.9), fill, false),
  ]));
}
export function reeds(list: [number, number, number][], p = 'reeds'): PicturePart {
  return part(p, t('reeds', 'bông lau'), 'scenery', list.flatMap(([x, y, h]) => [I(`M${x} ${y}C${x - 1} ${y - h * 0.5} ${x + 1} ${y - h * 0.8} ${x + 2} ${y - h}`, 'picLeafDark', 0.9), R(rrect(x - 2, y - h * 0.86, 7, h * 0.24, 3.5), 'picBrown', 2)]));
}
export function stones(list: [number, number, number][], p = 'stones', label = t('stepping stones', 'đá lát')): PicturePart {
  return part(p, label, 'scenery', list.map(([x, y, r]) => R(ellipse(x, y, r, r * 0.42), 'picStone', 2)));
}
export function rocks(list: [number, number, number][], p = 'rocks'): PicturePart {
  return part(p, t('rocks', 'tảng đá'), 'scenery', list.flatMap(([x, y, r]) => [R(blob([[x - r, y], [x - r * 0.7, y - r * 0.8], [x + r * 0.2, y - r], [x + r, y - r * 0.3], [x + r, y]], true, 0.9), 'picRock', 5), castShadow(x + r * 0.5, y + 1, r * 0.8, undefined, x)]));
}
export function shells(list: [number, number][], p = 'shells'): PicturePart {
  return part(p, t('seashells', 'vỏ sò'), 'prop', list.flatMap(([x, y]) => [R(`M${x - 7} ${y}C${x - 8} ${y - 10} ${x + 8} ${y - 10} ${x + 7} ${y}Z`, 'picPink', 2), I(`M${x} ${y}L${x} ${y - 6}M${x - 3} ${y}L${x - 4} ${y - 5}M${x + 3} ${y}L${x + 4} ${y - 5}`, 'picPlaid', 0.5)]));
}
export function mountains(h: number, list: [number, number, number][], p = 'mountains'): PicturePart {
  return part(p, t('mountains', 'núi xa'), 'scenery', list.flatMap(([x, w, top]) => [
    R(poly([[x - w, h + 1], [x, top], [x + w, h + 1]]), 'picMountain', false),
    R(poly([[x - w * 0.28, top + (h - top) * 0.28], [x, top], [x + w * 0.28, top + (h - top) * 0.28], [x + w * 0.1, top + (h - top) * 0.22], [x - w * 0.08, top + (h - top) * 0.3]]), 'picSnow', false),
  ]));
}
/** A small log lying on the ground. */
export function log(x: number, y: number, w: number, p = 'log'): PicturePart {
  return part(p, t('mossy log', 'khúc gỗ phủ rêu'), 'scenery', [R(rrect(x, y - 14, w, 14, 7), 'picTrunk', 3), R(ellipse(x + w - 4, y - 7, 5, 7), 'picCream', false), R(blob([[x + 6, y - 13], [x + w * 0.4, y - 19], [x + w * 0.7, y - 13]], true, 0.9), 'picLeaf', false), castShadow(x + w * 0.6, y + 1, w * 0.5, undefined, x)]);
}
export function seaweed(list: [number, number, number][], p = 'seaweed'): PicturePart {
  return part(p, t('seaweed', 'rong biển'), 'scenery', list.map(([x, y, h]) => R(`M${x - 4} ${y}C${x - 10} ${y - h * 0.3} ${x + 4} ${y - h * 0.6} ${x - 2} ${y - h}C${x + 8} ${y - h * 0.6} ${x - 2} ${y - h * 0.3} ${x + 5} ${y}Z`, 'picSeaweed', 3)));
}
export function coral(x: number, y: number, s: number, p = 'coral'): PicturePart {
  return part(p, t('coral', 'san hô'), 'scenery', [R(`M${x - 4 * s} ${y}L${x - 3 * s} ${y - 10 * s}C${x - 12 * s} ${y - 14 * s} ${x - 12 * s} ${y - 24 * s} ${x - 8 * s} ${y - 26 * s}C${x - 5 * s} ${y - 20 * s} ${x - 3 * s} ${y - 18 * s} ${x - 1 * s} ${y - 16 * s}L${x} ${y - 30 * s}C${x + 4 * s} ${y - 30 * s} ${x + 4 * s} ${y - 20 * s} ${x + 3 * s} ${y - 14 * s}C${x + 8 * s} ${y - 18 * s} ${x + 12 * s} ${y - 20 * s} ${x + 12 * s} ${y - 14 * s}C${x + 10 * s} ${y - 10 * s} ${x + 6 * s} ${y - 8 * s} ${x + 4 * s} ${y - 6 * s}L${x + 4 * s} ${y}Z`, 'picCoral', 4)]);
}
export function bubbles(list: [number, number, number][], p = 'bubbles'): PicturePart {
  return part(p, t('bubbles', 'bong bóng'), 'prop', list.flatMap(([x, y, r]) => [R(ellipse(x, y, r), 'picGlass', false), S(ellipse(x - r * 0.35, y - r * 0.35, r * 0.25), 'white')]));
}

/* ------------------------------------------- sky things ------------------------------------------- */

export function moon(x: number, y: number, r: number, p = 'moon'): PicturePart {
  return part(p, t('moon', 'mặt trăng'), 'scenery', [R(ellipse(x, y, r), 'picMoon', Math.round(r * 0.25)), S(ellipse(x - r * 0.3, y + r * 0.2, r * 0.18), 'picMoonDust'), S(ellipse(x + r * 0.3, y - r * 0.25, r * 0.12), 'picMoonDust')]);
}
export function sunDisc(x: number, y: number, r: number, p = 'sun'): PicturePart {
  return part(p, t('sun', 'mặt trời'), 'scenery', [R(ellipse(x, y, r), 'picYellow', false), I(`M${x + r * 1.3} ${y}L${x + r * 1.7} ${y}M${x} ${y + r * 1.3}L${x} ${y + r * 1.7}M${x + r} ${y + r}L${x + r * 1.3} ${y + r * 1.3}`, 'picOrange', 0.8)]);
}
export const stars = (list: [number, number, number][], p = 'stars'): PicturePart => part(p, t('stars', 'ngôi sao'), 'scenery', list.map(([x, y, r]) => R(twinkle(x, y, r), 'sparkle', false)));
export function planet(x: number, y: number, r: number, p = 'planet'): PicturePart {
  return part(p, t('ringed planet', 'hành tinh có vành'), 'scenery', [R(ellipse(x, y, r), 'picPlanet', Math.round(r * 0.3)), R(`${ellipse(x, y + 1, r * 1.7, r * 0.42)}${ellipse(x, y + 1, r * 1.25, r * 0.24)}`, 'picRing', false)]);
}
export function earth(x: number, y: number, r: number, p = 'earth'): PicturePart {
  const c = ellipse(x, y, r);
  return part(p, t('the Earth', 'Trái Đất'), 'scenery', [R(c, 'picEarth', Math.round(r * 0.3)), R(blob([[x - r * 0.6, y - r * 0.2], [x - r * 0.1, y - r * 0.6], [x + r * 0.2, y - r * 0.1], [x - r * 0.2, y + r * 0.4]], true, 0.9), 'picLeaf', false, { clip: c })]);
}
export const nebula = (list: [number, number, number][], p = 'nebula'): PicturePart =>
  part(p, t('purple space clouds', 'mây vũ trụ tím'), 'scenery', list.map(([x, y, r]) => R(scallop(x, y, r, r * 1.3, 6, 0.4), 'picNebula', false)));

/* ------------------------------------------- places ------------------------------------------- */

/** Winding dirt path from the horizon (narrow) to the bottom edge (wide). */
export function dirtPath(h: number, x: number, p = 'path'): PicturePart {
  return part(p, t('winding path', 'con đường mòn'), 'scenery', [R(`M${x - 3} ${h + 2}C${x - 30} ${h + 24} ${x + 26} ${h + 44} ${x - 24} 240L${x + 26} 240C${x + 52} ${h + 44} ${x - 6} ${h + 24} ${x + 4} ${h + 2}Z`, 'picSand', false)]);
}
export function island(x: number, h: number, w: number, p = 'island'): PicturePart {
  return part(p, t('island with a lighthouse', 'đảo nhỏ có ngọn hải đăng'), 'scenery', [
    R(`M${x - w} ${h}C${x - w * 0.5} ${h - 12} ${x + w * 0.4} ${h - 14} ${x + w} ${h}Z`, 'picHill', false),
    R(rrect(x - 3, h - 30, 6, 20, 1.5), 'white', false),
    R(rrect(x - 3, h - 22, 6, 4, 0), 'picRed', false),
    R(`M${x - 4} ${h - 30}L${x} ${h - 36}L${x + 4} ${h - 30}Z`, 'picRed', false),
  ]);
}
export function cliffs(h: number, p = 'cliffs'): PicturePart {
  return part(p, t('cliffs with a lighthouse', 'vách đá có ngọn hải đăng'), 'scenery', [
    R(`M0 ${h - 22}C14 ${h - 26} 30 ${h - 22} 38 ${h - 14}L46 ${h + 6}L0 ${h + 6}Z`, 'picCliff', 4),
    R(`M0 ${h - 24}C14 ${h - 28} 30 ${h - 24} 38 ${h - 16}C26 ${h - 18} 12 ${h - 20} 0 ${h - 18}Z`, 'picGrass', false),
    R(`M240 ${h - 30}C224 ${h - 34} 206 ${h - 30} 198 ${h - 20}L188 ${h + 6}L240 ${h + 6}Z`, 'picCliff', 4),
    R(`M240 ${h - 32}C224 ${h - 36} 206 ${h - 32} 198 ${h - 22}C210 ${h - 25} 226 ${h - 27} 240 ${h - 25}Z`, 'picGrass', false),
    R(rrect(214, h - 64, 10, 34, 2), 'white', 2),
    R(rrect(214, h - 52, 10, 6, 0), 'picRed', false),
    R(`M212 ${h - 64}L219 ${h - 72}L226 ${h - 64}Z`, 'picRed', false),
  ]);
}
export function pier(x: number, h: number, p = 'pier'): PicturePart {
  return part(p, t('wooden pier', 'cầu tàu gỗ'), 'scenery', [
    R(`M${x - 40} ${h + 30}L${x + 12} ${h + 4}L${x + 20} ${h + 8}L${x - 30} ${h + 36}Z`, 'picWood', 3),
    ...[0, 1, 2].map((i) => R(rrect(x - 34 + i * 20, h + 4 - i * 10, 5, 24, 1.5), 'picTrunk', false)),
    I(`M${x - 32} ${h + 8}C${x - 22} ${h + 6} ${x - 16} ${h + 2} ${x - 10} ${h - 2}C${x - 4} ${h - 4} ${x} ${h - 8} ${x + 8} ${h - 10}`, 'picBrown', 0.7),
  ]);
}
export function buoys(list: [number, number][], p = 'buoys'): PicturePart {
  return part(p, t('buoys', 'phao nổi'), 'prop', list.flatMap(([x, y]) => [R(ellipse(x, y, 6), 'picRed', 2), I(`M${x - 6} ${y}L${x + 6} ${y}`, 'white', 0.8)]));
}
export function bridge(x: number, h: number, w: number, p = 'bridge'): PicturePart {
  return part(p, t('little bridge', 'cây cầu nhỏ'), 'scenery', [R(`M${x - w} ${h + 6}C${x - w * 0.5} ${h - 8} ${x + w * 0.5} ${h - 8} ${x + w} ${h + 6}L${x + w} ${h + 9}C${x + w * 0.5} ${h - 3} ${x - w * 0.5} ${h - 3} ${x - w} ${h + 9}Z`, 'picWood', false), I(`M${x - w} ${h}C${x - w * 0.5} ${h - 14} ${x + w * 0.5} ${h - 14} ${x + w} ${h}`, 'picBrown', 0.7)]);
}
export function fence(h: number, x0: number, x1: number, p = 'fence'): PicturePart {
  const out: ArtLayer[] = [R(rrect(x0, h - 14, x1 - x0, 4, 1), 'picFence', false)];
  for (let x = x0 + 4; x < x1; x += 14) out.push(R(`M${x} ${h + 4}L${x} ${h - 20}L${x + 4} ${h - 24}L${x + 8} ${h - 20}L${x + 8} ${h + 4}Z`, 'picFence', 2));
  return part(p, t('picket fence', 'hàng rào trắng'), 'scenery', out);
}
/** Patchwork fields seen from high up (fills the ground). */
export function fields(h: number, p = 'fields'): PicturePart {
  const rows = [h, h + 22, h + 50, 240];
  const fills = ['picFieldA', 'picFieldB', 'picFieldC', 'picFieldD'];
  const out: ArtLayer[] = [];
  let k = 0;
  for (let r = 0; r < 3; r++) for (let c = 0; c < 4; c++) {
    const y0 = rows[r]!;
    const y1 = rows[r + 1]!;
    const x0 = c * 60 + (r % 2) * 14 - 14;
    out.push(R(poly([[Math.max(0, x0), y0], [Math.min(240, x0 + 64), y0], [Math.min(240, x0 + 70), y1], [Math.max(0, x0 + 4), y1]]), fills[k++ % 4]!, false));
  }
  return part(p, t('patchwork fields', 'những thửa ruộng'), 'ground', out, { colour: t('Colour each *field* a different colour: far ones paler', 'Tô mỗi *thửa ruộng* một màu: ruộng xa thì nhạt hơn') });
}
export function river(h: number, p = 'river'): PicturePart {
  return part(p, t('river', 'dòng sông'), 'scenery', [R(`M150 ${h + 1}C120 ${h + 20} 190 ${h + 40} 140 ${h + 60}C110 ${h + 72} 60 ${h + 80} 30 240L62 240C80 ${h + 84} 130 ${h + 76} 156 ${h + 62}C202 ${h + 40} 132 ${h + 20} 158 ${h + 1}Z`, 'picRiver', false)]);
}
export function dunes(h: number, p = 'dunes'): PicturePart {
  return part(p, t('sand dunes', 'đồi cát'), 'scenery', [R(`M0 ${h - 18}C30 ${h - 30} 60 ${h - 10} 90 ${h + 1}L0 ${h + 1}Z`, 'picSandNear', 4), R(`M240 ${h - 22}C200 ${h - 34} 170 ${h - 10} 140 ${h + 1}L240 ${h + 1}Z`, 'picSandNear', 4)]);
}
export function roofs(h: number, p = 'roofs'): PicturePart {
  return part(p, t('rooftops', 'mái nhà'), 'scenery', [
    R(rrect(0, h - 20, 40, 30, 0), 'picHouseWall', false),
    R(`M-4 ${h - 18}L22 ${h - 44}L48 ${h - 18}Z`, 'picRoof', 4),
    R(rrect(6, h - 46, 8, 14, 1), 'picBrown', false),
    R(rrect(206, h - 16, 34, 26, 0), 'picHouseWall', false),
    R(`M196 ${h - 14}L222 ${h - 38}L246 ${h - 14}Z`, 'picRoof', 4),
  ]);
}
/* ------------------------------------------- indoors ------------------------------------------- */

/** A window on the wall with a view: 'snow' (snowy garden) or 'night' (stars). */
export function windowPane(x: number, y: number, w: number, h: number, view: 'snow' | 'night' | 'day' = 'day', p = 'window'): PicturePart {
  const glass = view === 'night' ? 'picNight' : 'picSky';
  const out: ArtLayer[] = [R(rrect(x, y, w, h, 3), 'picWood', 3), R(rrect(x + 5, y + 5, w - 10, h - 10, 2), glass, false)];
  if (view === 'snow') out.push(R(`M${x + 5} ${y + h * 0.72}C${x + w * 0.4} ${y + h * 0.64} ${x + w * 0.7} ${y + h * 0.7} ${x + w - 5} ${y + h * 0.66}L${x + w - 5} ${y + h - 5}L${x + 5} ${y + h - 5}Z`, 'picSnow', false));
  if (view === 'night') out.push(R(twinkle(x + w * 0.35, y + h * 0.3, 4), 'sparkle', false));
  out.push(I(`M${x + w / 2} ${y + 5}L${x + w / 2} ${y + h - 5}M${x + 5} ${y + h / 2}L${x + w - 5} ${y + h / 2}`, 'picBrown', 1));
  return part(p, view === 'snow' ? t('frosty window', 'cửa sổ có tuyết') : view === 'night' ? t('night window', 'cửa sổ đêm sao') : t('window', 'cửa sổ'), 'scenery', out);
}
export function curtains(h: number, p = 'curtains'): PicturePart {
  return part(p, t('striped curtains', 'rèm cửa kẻ sọc'), 'scenery', [
    R(`M0 0L30 0C26 40 34 ${h * 0.6} 22 ${h}L0 ${h}Z`, 'picCurtain', 4),
    R(`M240 0L210 0C214 40 206 ${h * 0.6} 218 ${h}L240 ${h}Z`, 'picCurtain', 4),
    I(`M12 4C10 50 14 ${h * 0.6} 10 ${h - 4}M228 4C230 50 226 ${h * 0.6} 230 ${h - 4}`, 'picCurtainStripe', 1.6),
  ]);
}
export function books(x: number, y: number, p = 'books'): PicturePart {
  return part(p, t('pile of books', 'chồng sách'), 'prop', [R(rrect(x, y - 8, 34, 8, 2), 'picBook1', 2), R(rrect(x + 3, y - 16, 30, 8, 2), 'picBook2', 2), R(rrect(x + 1, y - 23, 28, 7, 2), 'picBook3', 2), castShadow(x + 24, y + 1, 18, undefined, x)]);
}
export const placemat = (x: number, y: number, rx: number, p = 'placemat'): PicturePart =>
  part(p, t('knitted placemat', 'tấm lót đan'), 'prop', [R(ellipse(x, y, rx, rx * 0.22), 'picCream', false), I(`M${x - rx * 0.7} ${y}C${x - rx * 0.3} ${y - rx * 0.12} ${x + rx * 0.3} ${y - rx * 0.12} ${x + rx * 0.7} ${y}`, 'picWood', 0.6)]);
export function armchair(x: number, y: number, p = 'armchair'): PicturePart {
  return part(p, t('armchair with a blanket', 'ghế bành và chăn'), 'scenery', [
    R(rrect(x - 26, y - 56, 52, 40, 12), 'picChair', 5),
    R(rrect(x - 32, y - 30, 64, 26, 8), 'picChair', 5),
    R(rrect(x - 26, y - 6, 6, 8, 1), 'picBrown', false),
    R(rrect(x + 20, y - 6, 6, 8, 1), 'picBrown', false),
    R(`M${x + 2} ${y - 54}L${x + 18} ${y - 56}L${x + 24} ${y - 14}L${x + 8} ${y - 12}Z`, 'picPlaid', 2),
    I(`M${x + 6} ${y - 44}L${x + 21} ${y - 46}M${x + 8} ${y - 30}L${x + 23} ${y - 31}M${x + 12} ${y - 54}L${x + 16} ${y - 13}`, 'picCream', 0.6),
    castShadow(x + 14, y + 2, 34, undefined, x),
  ]);
}
export function sideTable(x: number, y: number, p = 'lampTable'): PicturePart {
  return part(p, t('little table and lamp', 'bàn nhỏ và đèn'), 'prop', [
    R(rrect(x - 12, y - 24, 24, 5, 2), 'picWood', 2),
    R(rrect(x - 10, y - 19, 3, 19, 1), 'picBrown', false),
    R(rrect(x + 7, y - 19, 3, 19, 1), 'picBrown', false),
    R(rrect(x - 1.5, y - 38, 3, 14, 1), 'picBrown', false),
    R(`M${x - 9} ${y - 38}L${x - 5} ${y - 50}L${x + 5} ${y - 50}L${x + 9} ${y - 38}Z`, 'picYellow', 2),
    S(ellipse(x, y - 36, 14, 5), 'picGlow', 0.6),
  ]);
}
export function brushJar(x: number, y: number, p = 'brushJar'): PicturePart {
  return part(p, t('jar of brushes', 'lọ cọ vẽ'), 'prop', [
    R(rotate(rrect(x - 4, y - 52, 3, 34, 1), -10, x, y - 20), 'picWood', false),
    R(rotate(rrect(x + 2, y - 56, 3, 38, 1), 8, x, y - 20), 'picRed', false),
    R(rrect(x - 12, y - 26, 24, 26, 5), 'picGlass', 3),
    castShadow(x + 8, y + 1, 14, undefined, x),
  ]);
}
/** A soft corner plant (leaves hanging into the picture from a top corner). */
export function plantCorner(side: 'left' | 'right', p = 'plant'): PicturePart {
  const s = side === 'left' ? 1 : -1;
  const x = side === 'left' ? 0 : 240;
  return part(p, t('leafy plant', 'chậu lá'), 'scenery', [0, 1, 2].map((i) => R(blob([[x, 4 + i * 16], [x + s * (30 + i * 6), 12 + i * 20], [x + s * (14 + i * 4), 22 + i * 18]], true, 0.9), i % 2 ? 'picLeafDark' : 'picLeaf', 2)));
}
export function bench(x: number, y: number, p = 'bench'): PicturePart {
  return part(p, t('park bench', 'ghế công viên'), 'scenery', [R(rrect(x - 22, y - 22, 44, 6, 2), 'picWood', 2), R(rrect(x - 22, y - 13, 44, 5, 2), 'picWood', 2), R(rrect(x - 19, y - 8, 4, 8, 1), 'picBrown', false), R(rrect(x + 15, y - 8, 4, 8, 1), 'picBrown', false), castShadow(x + 10, y + 1, 26, undefined, x)]);
}
export function pool(x: number, y: number, rx: number, p = 'pool'): PicturePart {
  return part(p, t('paddling pool', 'bể bơi phao'), 'prop', [R(ellipse(x, y, rx, rx * 0.3), 'white', 3), R(ellipse(x, y + 1, rx * 0.8, rx * 0.2), 'picSea', false)]);
}
export function towel(x: number, y: number, p = 'towel'): PicturePart {
  return part(p, t('beach towel', 'khăn tắm biển'), 'prop', [R(poly([[x, y], [x + 38, y - 8], [x + 52, y + 8], [x + 12, y + 18]]), 'picTowel', false), I(`M${x + 9} ${y + 4}L${x + 45} ${y - 4}M${x + 12} ${y + 11}L${x + 48} ${y + 3}`, 'white', 1.4)]);
}
export function bucket(x: number, y: number, p = 'bucket'): PicturePart {
  return part(p, t('sand bucket', 'xô cát'), 'prop', [R(`M${x - 10} ${y - 18}L${x + 10} ${y - 18}L${x + 7} ${y}L${x - 7} ${y}Z`, 'picBucket', 3), I(`M${x - 10} ${y - 18}C${x - 8} ${y - 30} ${x + 8} ${y - 30} ${x + 10} ${y - 18}`, 'picBrown', 0.6), castShadow(x + 6, y + 1, 10, undefined, x)]);
}
export function pot(x: number, y: number, p = 'pot'): PicturePart {
  return part(p, t('flower pot', 'chậu hoa'), 'prop', [I(`M${x} ${y - 16}L${x} ${y - 30}`, 'picLeafDark', 0.9), R(scallop(x, y - 33, 6, 6, 5), 'picPink', false), R(`M${x - 11} ${y - 18}L${x + 11} ${y - 18}L${x + 8} ${y}L${x - 8} ${y}Z`, 'picPot', 3), castShadow(x + 7, y + 1, 11, undefined, x)]);
}
